/**
 * Notification Saga
 * Handles async notification API calls
 */

import { call, put, takeLatest } from 'redux-saga/effects';
import requestHandler from '../../../utils/requestHandler';
import {
  checkServiceStatusRequest,
  checkServiceStatusSuccess,
  checkServiceStatusFailure,
  sendPortalCredentialsRequest,
  sendPortalCredentialsSuccess,
  sendPortalCredentialsFailure,
  sendAppointmentReminderRequest,
  sendAppointmentReminderSuccess,
  sendAppointmentReminderFailure,
  sendReportReadyRequest,
  sendReportReadySuccess,
  sendReportReadyFailure,
  sendCustomNotificationRequest,
  sendCustomNotificationSuccess,
  sendCustomNotificationFailure,
  getPatientNotificationsRequest,
  getPatientNotificationsSuccess,
  getPatientNotificationsFailure,
  getAllNotificationsRequest,
  getAllNotificationsSuccess,
  getAllNotificationsFailure,
} from './slice';

const API_BASE = '/notification';

// Check WhatsApp Service Status
function* checkServiceStatusSaga() {
  try {
    const response = yield call(requestHandler, {
      method: 'GET',
      url: `${API_BASE}/status`,
    });
    if (response.data.success) {
      yield put(checkServiceStatusSuccess(response.data));
    } else {
      yield put(checkServiceStatusFailure(response.data.message));
    }
  } catch (error) {
    yield put(checkServiceStatusFailure(error.message || 'Failed to check service status'));
  }
}

// Send Portal Credentials via WhatsApp
function* sendPortalCredentialsSaga(action) {
  try {
    const response = yield call(requestHandler, {
      method: 'POST',
      url: `${API_BASE}/portal-credentials`,
      data: action.payload,
    });
    if (response.data.success) {
      yield put(sendPortalCredentialsSuccess(response.data));
    } else {
      yield put(sendPortalCredentialsFailure(response.data.message));
    }
  } catch (error) {
    yield put(sendPortalCredentialsFailure(error.message || 'Failed to send portal credentials'));
  }
}

// Send Appointment Reminder
function* sendAppointmentReminderSaga(action) {
  try {
    const response = yield call(requestHandler, {
      method: 'POST',
      url: `${API_BASE}/appointment-reminder`,
      data: action.payload,
    });
    if (response.data.success) {
      yield put(sendAppointmentReminderSuccess(response.data));
    } else {
      yield put(sendAppointmentReminderFailure(response.data.message));
    }
  } catch (error) {
    yield put(sendAppointmentReminderFailure(error.message || 'Failed to send appointment reminder'));
  }
}

// Send Report Ready Notification
function* sendReportReadySaga(action) {
  try {
    const response = yield call(requestHandler, {
      method: 'POST',
      url: `${API_BASE}/report-ready`,
      data: action.payload,
    });
    if (response.data.success) {
      yield put(sendReportReadySuccess(response.data));
    } else {
      yield put(sendReportReadyFailure(response.data.message));
    }
  } catch (error) {
    yield put(sendReportReadyFailure(error.message || 'Failed to send report ready notification'));
  }
}

// Send Custom Notification
function* sendCustomNotificationSaga(action) {
  try {
    const response = yield call(requestHandler, {
      method: 'POST',
      url: `${API_BASE}/custom`,
      data: action.payload,
    });
    if (response.data.success) {
      yield put(sendCustomNotificationSuccess(response.data));
    } else {
      yield put(sendCustomNotificationFailure(response.data.message));
    }
  } catch (error) {
    yield put(sendCustomNotificationFailure(error.message || 'Failed to send notification'));
  }
}

// Get Patient Notifications
function* getPatientNotificationsSaga(action) {
  try {
    const { patientId, page = 1, limit = 20 } = action.payload;
    const response = yield call(requestHandler, {
      method: 'GET',
      url: `${API_BASE}/patient/${patientId}?page=${page}&limit=${limit}`,
    });
    if (response.data.success) {
      yield put(getPatientNotificationsSuccess(response.data));
    } else {
      yield put(getPatientNotificationsFailure(response.data.message));
    }
  } catch (error) {
    yield put(getPatientNotificationsFailure(error.message || 'Failed to fetch notifications'));
  }
}

// Get All Notifications (Admin)
function* getAllNotificationsSaga(action) {
  try {
    const { page = 1, limit = 50, status, type, channel } = action.payload || {};
    let url = `${API_BASE}/all?page=${page}&limit=${limit}`;
    if (status) url += `&status=${status}`;
    if (type) url += `&type=${type}`;
    if (channel) url += `&channel=${channel}`;

    const response = yield call(requestHandler, {
      method: 'GET',
      url,
    });
    if (response.data.success) {
      yield put(getAllNotificationsSuccess(response.data));
    } else {
      yield put(getAllNotificationsFailure(response.data.message));
    }
  } catch (error) {
    yield put(getAllNotificationsFailure(error.message || 'Failed to fetch notifications'));
  }
}

// Root notification saga
export default function* notificationSaga() {
  yield takeLatest(checkServiceStatusRequest.type, checkServiceStatusSaga);
  yield takeLatest(sendPortalCredentialsRequest.type, sendPortalCredentialsSaga);
  yield takeLatest(sendAppointmentReminderRequest.type, sendAppointmentReminderSaga);
  yield takeLatest(sendReportReadyRequest.type, sendReportReadySaga);
  yield takeLatest(sendCustomNotificationRequest.type, sendCustomNotificationSaga);
  yield takeLatest(getPatientNotificationsRequest.type, getPatientNotificationsSaga);
  yield takeLatest(getAllNotificationsRequest.type, getAllNotificationsSaga);
}
